import React, { useState } from 'react';

import { Card, Typography } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WorkflowAssigneesChooser } from 'src/components/workflow/assignees';
import { PriorityDisplay, WorkflowPriority } from 'src/components/workflow/workflow-list/cells';
import {
  getWorkflowTypeIcon,
  getWorkflowTypeLabel,
} from 'src/components/workflow/workflow-list/workflow-type';
import { Apollo } from 'src/gql/generated';
import { openLinkFromAnchorClick } from 'src/helpers';
import type { WorkflowFileSubset } from 'src/hooks';
import { createClampStyles } from 'src/theme';
import type { LabelSubset, UserWithOrg } from 'src/types';
import type { WorkflowPermissionSubset } from 'src/types/apollo/workflow-permission';
import { makeStyles } from 'tss-react/mui';

import { WorkflowCardLabels } from './components/WorkflowCardLabels';
import { WorkflowCardMenuButton } from './components/WorkflowCardMenuButton';
import { WorkflowFileChips } from './components/WorkflowFileChips';
import type { ProgressTask } from './task-progress';

const useStyles = makeStyles()(theme => ({
  card: {
    /** The link is a transparent overlay, so the ring has to be drawn on the card. */
    '&:has(a:focus-visible)': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:hover': {
      boxShadow: theme.shadows[3],
    },
    '&:last-child': {
      marginBottom: theme.spacing(1),
    },
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    background: '#FFF',
    borderRadius: 8,
    boxSizing: 'border-box',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    overflow: 'unset',
    padding: theme.spacing(2.5),
    position: 'relative',
  },
  cardRoot: { overflow: 'unset' },
  headerRow: {
    // Top-aligned so the menu and assignees stay put when the title wraps to a second line.
    alignItems: 'flex-start',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  /** Sits above the stretched link so its own click handler still wins. */
  interactive: {
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    zIndex: 2,
  },
  interactiveBlock: {
    position: 'relative',
    zIndex: 2,
  },
  metaIcon: {
    alignItems: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexShrink: 0,
  },
  metaItem: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(0.75),
    maxWidth: '100%',
    minWidth: 0,
  },
  metaLabel: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.pxToRem(12),
  },
  metaRow: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    width: '100%',
  },
  stretchedLink: {
    // The visible ring is drawn on the card via `:has(a:focus-visible)`.
    '&:focus-visible': { outline: 'none' },
    borderRadius: 'inherit',
    display: 'block',
    inset: 0,
    position: 'absolute',
    textDecoration: 'none',
    zIndex: 1,
  },
  workflowTitle: {
    ...createClampStyles(2),
    flex: 1,
    fontSize: '18px',
    fontWeight: 600,
    // Allows the clamped title to shrink below its intrinsic width.
    minWidth: 0,
  },
}));

export interface WorkflowCardWorkflow extends Pick<
  Apollo.Workflow,
  | '__typename'
  | 'commentCount'
  | 'completedAt'
  | 'completedStepCount'
  | 'id'
  | 'name'
  | 'priority'
  | 'startedAt'
  | 'status'
  | 'totalStepCount'
  | 'updatedAt'
  | 'variant'
> {
  column: Pick<Apollo.Column, 'category' | 'color' | 'id' | 'name' | 'sortKey'>;
  files: WorkflowFileSubset[];
  labels: LabelSubset[];
  owner: UserWithOrg;
  permissions: WorkflowPermissionSubset[];
  phases: Array<
    Pick<Apollo.Phase, 'completedAt' | 'id' | 'name' | 'order' | 'startedAt'> & {
      column: Pick<Apollo.Column, 'id'>;
      __typename?: 'NonSigningPhase' | 'SigningPhase';
    }
  >;
  sourceWorkflow: Pick<Apollo.Workflow, 'id' | 'name' | 'variant' | '__typename'> | null;
  // Only the board query selects tasks; community cards leave it out.
  tasks?: ProgressTask[];
}

interface WorkflowCardProps extends React.HTMLAttributes<HTMLElement> {
  workflow: WorkflowCardWorkflow;
  /** Omitted by the drag overlay, which is a visual ghost rather than a real card. */
  linkTo?: string;
}

export function WorkflowCard({ linkTo, workflow, ...rest }: WorkflowCardProps) {
  const { classes } = useStyles();
  const [mouseIn, setMouseIn] = useState<boolean>(false);
  const [updateWorkflow] = Apollo.useUpdateWorkflowMutation();
  const sourceWorkflow = workflow.sourceWorkflow
    ? { id: workflow.sourceWorkflow.id, name: workflow.sourceWorkflow.name }
    : null;
  const workflowTypeLabel = getWorkflowTypeLabel(sourceWorkflow);
  const title = workflow.name || 'Untitled Workflow';

  return (
    <Card
      className={classes.card}
      classes={{ root: classes.cardRoot }}
      raised={mouseIn}
      onMouseEnter={() => setMouseIn(true)}
      onMouseLeave={() => setMouseIn(false)}
      {...rest}
    >
      {linkTo && (
        <Link
          aria-label={`Open a workflow: ${title}`}
          className={classes.stretchedLink}
          draggable={false}
          to={linkTo}
          onClick={e => openLinkFromAnchorClick(e, linkTo, { title })}
        />
      )}
      <div className={classes.headerRow}>
        <Typography className={classes.workflowTitle} variant="body1">
          {title}
        </Typography>
        <div className={classes.interactive}>
          <WorkflowCardMenuButton show={mouseIn} workflowId={workflow.id} />
        </div>
        <div className={classes.interactive}>
          <WorkflowAssigneesChooser max={2} workflow={workflow} />
        </div>
      </div>

      <div className={classes.metaRow}>
        <div className={classes.interactive}>
          <WorkflowPriority
            priority={workflow.priority}
            renderTrigger={priority => <PriorityDisplay priority={priority} />}
            onPriorityChange={async priority => {
              await updateWorkflow({
                variables: {
                  input: { props: { priority }, workflowId: workflow.id },
                },
              });
            }}
          />
        </div>
        <CardMetaItem icon={getWorkflowTypeIcon(sourceWorkflow)} label={workflowTypeLabel} />
      </div>
      <div className={classes.interactiveBlock}>
        <WorkflowCardLabels workflow={workflow} />
      </div>
      <div className={classes.interactiveBlock}>
        <WorkflowFileChips files={workflow.files} workflowId={workflow.id} />
      </div>
    </Card>
  );
}

function CardMetaItem({ icon: Icon, label }: { icon: LucideIcon; label: string }) {
  const { classes } = useStyles();
  return (
    <div className={classes.metaItem}>
      <div className={classes.metaIcon}>
        <Icon size={14} />
      </div>
      <Typography noWrap className={classes.metaLabel} variant="body2">
        {label}
      </Typography>
    </div>
  );
}
